import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import mapboxgl from 'mapbox-gl';
import { useSelector, useDispatch } from 'react-redux';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { FaMap, FaPrint } from 'react-icons/fa';

import { Key } from './key';
import { mapState, getMapbox, hoveredParcelState } from './selectors';
import { HoveredParcel } from './hovered-parcel';
import { LayerWrapper } from './layer';
import { Layers } from './layers';
import mapStyleJSON from './map-style.json';

const onClick = (map, event, dispatch, reports) => {
  const { lngLat } = event;

  // if no reports are defined, use the jackson style report
  if (!reports || !reports.length) {
    dispatch({
      type: 'OPEN_MODAL',
      payload: {
        type: 'REPORTS',
        data: {
          config: {
            lngLat,
          },
        },
      },
    });
    return;
  }
  dispatch({
    type: 'OPEN_SIDE_PANEL',
    payload: {
      type: 'PARCEL',
      data: {
        config: {
          lngLat,
          report: reports.find(({ id }) => id === 'PARCEL_REPORT'),
        },
      },
    },
  });
};

const setMapSettings = (map, event, dispatch, id) => {
  const center = map.getCenter();
  const zoom = map.getZoom();

  dispatch({
    type: 'SET_MAP_SETTINGS',
    payload: {
      id,
      settings: {
        zoom,
        center,
      },
    },
  });
};

const Container = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  width: 100%;
`;

const ToggleSatellite = styled.div`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 150px;
  padding: 7px;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  height: 16px;
  width: 16px;
  border-radius: 4px;
`;

const PrintMap = styled.div`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 186px;
  padding: 7px;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  height: 16px;
  width: 16px;
  border-radius: 4px;
`;

const containerStyle = {
  height: '100%',
  width: '100%',
};

const draw = new MapboxDraw({
  displayControlsDefault: false,
});

/* eslint-disable react/style-prop-object */
let moving = false;
export const Map = ({ id = 'MAIN_MAP' }) => {
  const {
    reports,
    layers,
    center,
    zoom,
    tileSourceOptions,
    filters,
    hasNewLayers,
    drawEnabled,
    mapStyle,
    selectedSurveyLayers,
  } = useSelector(mapState);

  const Mapbox = useSelector(getMapbox);

  const dispatch = useDispatch();

  const [debouncedHover, cancel] = useDebouncedCallback((map, event) => {
    const { lngLat } = event;
    dispatch({
      type: 'GET_HOVERED_PARCEL',
      payload: {
        lngLat,
      },
    });
  }, 100);

  const updateArea = useCallback(
    (e) => {
      const data = draw.getAll();
      if (data.features.length > 0) {
        dispatch({
          type: 'SET_DRAW_AREA',
          payload: {
            data,
          },
        });
      } else if (e.type === 'draw.delete') {
        dispatch({
          type: 'CLEAR_DRAW_AREA',
        });
      }
    },
    [dispatch],
  );

  const onMoveEnd = useCallback(
    (...args) => {
      setMapSettings(...args, dispatch, id);
      dispatch({ type: 'CLEAR_LOADING_LAYERS' });
    },
    [dispatch, id],
  );

  const onMouseMove = useCallback(
    (...args) => {
      debouncedHover(...args);
    },
    [debouncedHover],
  );

  const clearHovered = useCallback(() => {
    dispatch({
      type: 'CLEAR_HOVERED_PARCEL',
    });
    cancel();
  }, [dispatch, cancel]);

  const onClickMap = useCallback(
    (...args) => {
      if (!drawEnabled && draw.getAll().features.length === 0) {
        onClick(...args, dispatch, reports);
      }
    },
    [dispatch, drawEnabled, reports],
  );

  const startTouching = useCallback(() => {
    moving = false;
  }, []);

  const startMoving = useCallback(() => {
    moving = true;
  }, []);

  const onDataLoading = useCallback(
    (_, layer) => {
      if (
        layer.sourceId === 'mapbox-gl-draw-cold' ||
        layer.sourceId === 'mapbox-gl-draw-hot' ||
        layer.sourceId === 'composite'
      ) {
        return null;
      }
      if (!layer.sourceId || layer.isSourceLoaded) return null;
      dispatch({
        type: 'START_LOADING_LAYER',
        payload: layer.sourceId,
      });
      return null;
    },
    [dispatch],
  );

  const onData = useCallback(
    (map, layer) => {
      if (
        layer.sourceId === 'mapbox-gl-draw-cold' ||
        layer.sourceId === 'mapbox-gl-draw-hot' ||
        layer.sourceId === 'composite'
      ) return;
      if (!layer.sourceId || !layer.isSourceLoaded) return;
      if (map.getLayer('road_layer-layer-raster')) map.moveLayer('road_layer-layer-raster');
      if (map.getLayer('road_label_layer-layer-raster')) map.moveLayer('road_label_layer-layer-raster');
      dispatch({
        type: 'STOP_LOADING_LAYER',
        payload: layer.sourceId,
      });
    },
    [dispatch],
  );

  const stopTouching = useCallback(() => {
    moving = false;
  }, []);

  const onTouchEnd = useCallback(
    (...args) => {
      if (!moving && !drawEnabled && draw.getAll().features.length === 0) {
        onClick(...args, dispatch, reports);
      }
    },
    [drawEnabled, dispatch, reports],
  );

  const onStyleLoad = useCallback(
    (map, ...args) => {
      // eslint-disable-next-line no-param-reassign
      map.getCanvas().style.cursor = 'pointer';

      map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-right');
      map.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
        }),
        'top-right',
      );
      map.addControl(new mapboxgl.NavigationControl());

      map.addControl(draw, 'top-right');
      if (drawEnabled) {
        draw.changeMode('draw_polygon');
      } else {
        draw.changeMode('simple_select');
      }
      map.on('draw.create', updateArea);
      map.on('draw.delete', updateArea);
      map.on('draw.update', updateArea);
      setMapSettings(map, ...args, dispatch, id);
    },
    [dispatch, drawEnabled, updateArea, id],
  );

  return (
    <Container>
      <Mapbox
        onStyleLoad={onStyleLoad}
        zoom={zoom}
        movingMethod="jumpTo"
        onDragEnd={onMoveEnd}
        onMouseMove={onMouseMove}
        onMouseOut={clearHovered}
        onDataLoading={onDataLoading}
        onData={onData}
        onBlur={clearHovered}
        onZoomEnd={onMoveEnd}
        onClick={onClickMap}
        onTouchStart={startTouching}
        onTouchMove={startMoving}
        onTouchCancel={stopTouching}
        onTouchEnd={onTouchEnd}
        containerStyle={containerStyle}
        center={center}
        style={mapStyle === 'light-v10' ? mapStyleJSON : `mapbox://styles/mapbox/${mapStyle}`}
      >
        <Layers
          layers={Object.values(layers)}
          filters={filters}
          tileSourceOptions={tileSourceOptions}
          hasNewLayers={hasNewLayers}
          forceDots={zoom < 12.8}
        />
        <HoveredGeometryLayer />
      </Mapbox>
      <Key layers={layers} selectedSurveyLayers={selectedSurveyLayers} />
      <HoveredParcel />
      <ToggleSatellite
        onClick={() => dispatch({
          type: 'SET_MAP_STYLE',
          payload: {
            style: mapStyle === 'light-v10' ? 'satellite-v9' : 'light-v10',
          },
        })}
      >
        <FaMap />
      </ToggleSatellite>
      <PrintMap
        onClick={() => dispatch({
          type: 'OPEN_MODAL',
          payload: {
            type: 'PRINT_MAP',
            data: {
              Mapbox,
            },
          },
        })}
      >
        <FaPrint />
      </PrintMap>
    </Container>
  );
};

const HoveredGeometryLayer = () => {
  const state = useSelector(hoveredParcelState);
  const { layer, tileSourceOptions } = state;
  let { styleType } = state;

  if (!layer) return null;
  const { key, label } = layer;
  if (layer && !layer.forceDots) {
    styleType = 'line';
  }
  return (
    <LayerWrapper
      forceDots={styleType === 'circle'}
      key={`${key}-${label}-${layer.style.type}`}
      layer={layer}
      tileSourceOptions={tileSourceOptions}
    />
  );
};
